import { memo, useMemo } from 'react'
import styled from 'styled-components'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import OfferBadge from 'components/Common/OfferBadge'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import HeroOfferTileWrapper from './HeroOfferTileWrapper'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HeroVilaOfferDetails from './HeroVilaOfferDetails'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'

const MainImageSegment = styled.div`
  aspect-ratio: 3 / 2;

  ${mediaQueryUp.tablet} {
    aspect-ratio: 16 / 9;
  }

  ${mediaQueryUp.desktop} {
    aspect-ratio: unset;
  }
`

interface Props {
  offer: App.VillaOffer | App.VillaOfferSummary
  eagerLoadFirstImage?: boolean
  offerUrl: string
}

const imageParams: ImageParams = {
  mobileAspectRatio: '3:2',
  tabletAspectRatio: '16:9',
  desktopAspectRatio: '19:9',
  mobileWidth: '75vw',
  tabletWidth: '55vw',
  desktopWidth: '55vw',
  largeDesktopWidth: '764px',
  quality: 'good',
}

function HeroVillaOfferTile(props: Props) {
  const {
    offer,
    eagerLoadFirstImage,
    offerUrl,
  } = props

  const isMobileDevice = useIsMobileDevice()
  // Swiping through the offer tiles is tough when the image carousel can also swipe. So turn off the image carousel for mobile devices
  const images = useMemo(() => isMobileDevice ? [offer.images[0]] : offer.images, [isMobileDevice, offer.images])

  if (offer.isSoldOut) return null

  return (
    <ProductPaletteProvider product={offer}>
      <HeroOfferTileWrapper>
        <MainImageSegment>
          <ImageCarousel
            topLeft={<OfferPartnershipLabel offer={offer} />}
            topRight={<Group direction="horizontal" gap={4}>
              <TripGuard>
                <CSSBreakpoint max="tablet">
                  <BookmarkButton offer={offer} />
                </CSSBreakpoint>
              </TripGuard>
              {offer.badge && <OfferBadge badge={offer.badge} />}
            </Group>}
            images={images}
            imageParams={imageParams}
            eagerLoadFirstImage={eagerLoadFirstImage}
          />
        </MainImageSegment>
        <HeroVilaOfferDetails offer={offer} offerUrl={offerUrl} />
      </HeroOfferTileWrapper>
    </ProductPaletteProvider>
  )
}

export default memo(HeroVillaOfferTile)
